import { DocumentCard } from "@fluentui/react";
import { getNextColor } from "@fluentui/react-charting";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import React from "react";
import AIInsightsButtonWithPanel from "../../helper/AIInsightsButtonWithPanel";
import { CustomShimmer } from "../../helper/CustomShimmer";
import WarningBanner from "../../helper/WarningBanner";
import { CustomCardWithMetrics } from "../../SharedComponents/CustomCardWithMetrics";
import { CustomMetricsCard } from "../../SharedComponents/CustomMetricsCard";
import { ChartOption, MetricOptionProps } from "../../SharedComponents/Interfaces/CustomCardWithMetrics.interfaces";
import { getAbbrevatedValue, groupBy } from "../../Utils/js-common";
import { PRODUCT_VIEW_STYLES } from "../ProductView/Common/ProductView.styles";
import { CUSTOMER_VIEW_STYLES } from "./CustomerView.styles";

const SupportChart = (props: {
  error?: any;
  loading: boolean;
  options: MetricOptionProps<any>;
  onSelectedChartFilter: (filter: { filterKey: string; value: string }) => void;
  tenantId: any;
  tenantName: any;
}) => {
  // Properties
  const [metricFilters, setMetricFilters] = React.useState<{ [key: string]: any }>({});

  // Events
  const handleResetFilters = () => {
    setMetricFilters({});
    props.onSelectedChartFilter({
      filterKey: "",
      value: ""
    });
  };
  /**
   * onClick action for each datapoint in the chart
   */
  const handleDataPointClick = ({ field, title, value }: ChartOption) => {
    props.onSelectedChartFilter({
      filterKey: field,
      value: value
    });
    setMetricFilters((prev) => ({ ...prev, [title]: value }));
  };

  const calculateAverage = (data: any[], columnName: string) => {
    const filteredData = data.filter((item) => item[columnName]);
    if (filteredData.length === 0) return 0;

    const total = filteredData.reduce((sum, item) => sum + Number(item[columnName]), 0);
    const average = total / filteredData.length;

    return average;
  };

  const calculatePercentDSAT = (data: any[], columnName: string) => {
    const filteredData = data.filter((item) => item[columnName]);
    if (filteredData.length === 0) return 0;

    const total = filteredData.reduce((count, item) => {
      const value = Number(item[columnName]);
      return value === 1 || value === 2 ? count + 1 : count;
    }, 0);
    return (total / filteredData.length) * 100;
  };

  const calculatePercentile75 = (data: any[], columnName: string) => {
    if (data.length === 0) return 0;

    const times = data.map((item) => Number(item[columnName])).sort((a, b) => a - b);
    const index = Math.ceil(0.75 * times.length) - 1;

    return times[index];
  };

  // Metrics Calculation
  const filteredData = props.options.originalData.filter((item) => item["closedMonthYear"]);
  const groupByDate = groupBy(filteredData, "closedMonthYear");
  const dataByMonthYear = Object.keys(groupByDate).map((item) => {
    return {
      x: new Date(new Date(item).toISOString()),
      y: groupByDate[item].length
    };
  });
  dataByMonthYear.sort((a, b) => a.x.getTime() - b.x.getTime());
  const yAxisValues = dataByMonthYear.map((point: { y: any }) => point.y) ?? [];

  const totalVolume = props.options.filteredData.length;
  const totalEnterpriseCases = props.options.filteredData.filter(
    (item) => item["serviceOfferingLevel1"] == "Enterprise Support"
  ).length;
  const averageCSAT = calculateAverage(props.options.filteredData, "csat");
  const percentDSAT = calculatePercentDSAT(props.options.filteredData, "csat");
  const timeInCrisitSevPercentile75 = calculatePercentile75(
    props.options.filteredData.filter((item) => item["timeInCritsit"] && item["serviceRequestState"] == "Closed"),
    "timeInCritsit"
  );
  const timeInCrisitSevAPercentile75 = calculatePercentile75(
    props.options.filteredData.filter(
      (item) =>
        item["serviceRequestMaxSeverity"] == "A" && item["timeInCritsitA"] && item["serviceRequestState"] == "Closed"
    ),
    "timeInCritsitA"
  );
  const daysToCloesePercentile75 = calculatePercentile75(
    props.options.filteredData.filter((item) => item["serviceRequestState"] == "Closed"),
    "daysToClose"
  );

  // Data
  const metricdata = React.useMemo(
    () => [
      {
        title: "Total Volume",
        description: "Volume of support request by Closed date",
        currentValue: totalVolume.toString(),
        abbrevatedValue: getAbbrevatedValue(totalVolume),
        color: getNextColor(0)
      },
      {
        title: "Enterprise Cases",
        description: "Volume of support request by created date",
        currentValue: totalEnterpriseCases.toString(),
        abbrevatedValue: getAbbrevatedValue(totalEnterpriseCases),
        color: getNextColor(1)
      },
      {
        title: "CSAT",
        description: "The degree to which customer expectations of products and services are met or exceeded.",
        currentValue: averageCSAT.toString(),
        abbrevatedValue: getAbbrevatedValue(averageCSAT),
        color: getNextColor(2)
      },
      {
        title: "DSAT%",
        description:
          "The degree to which customer expectations of products and services are not met. Rating: 1 OR 2 only",
        currentValue: percentDSAT.toString(),
        abbrevatedValue: getAbbrevatedValue(percentDSAT),
        color: getNextColor(3)
      },
      {
        title: "TIC @P75",
        description:
          "This is presented on a scale, where P@75 is computed for each week based on closed cases and their respective maximum severity (1/A). We sum up the values based on the maximum severity of the SR and then perform a P@75 calculation on top of it. TIC Sev P@75 is represened in hours. The N for TIC could be counted twice for critsits. Since there could be SRs contributing to both Sev 1 and Sev A, that SR will be present in the N for each.",
        currentValue: timeInCrisitSevPercentile75.toString(),
        abbrevatedValue: getAbbrevatedValue(timeInCrisitSevPercentile75),
        color: getNextColor(5)
      },
      {
        title: "TIC Sev A @P75",
        description:
          "This is presented on a scale, where P@75 is computed for each week based on closed cases and their respective maximum severity (1/A). We sum up the values based on the maximum severity of the SR and then perform a P@75 calculation on top of it. TIC Sev A P@75 is represened in hours. The N for TIC could be counted twice for critsits. Since there could be SRs contributing to both Sev 1 and Sev A, that SR will be present in the N for each.",
        currentValue: timeInCrisitSevAPercentile75.toString(),
        abbrevatedValue: getAbbrevatedValue(timeInCrisitSevAPercentile75),
        color: getNextColor(4)
      },
      {
        title: "DTC @P75",
        description:
          "This is represented for all severity cases, and a  @P75 layer is applied  on the WOW closed SR volume. DTC is represented in days.",
        currentValue: daysToCloesePercentile75.toString(),
        abbrevatedValue: getAbbrevatedValue(daysToCloesePercentile75),
        color: getNextColor(6)
      }
    ],
    [
      totalVolume,
      averageCSAT,
      percentDSAT,
      timeInCrisitSevAPercentile75,
      timeInCrisitSevPercentile75,
      daysToCloesePercentile75
    ]
  );

  const displayContentsWithButton = (
    <FluentProvider theme={webLightTheme}>
      <div className={CUSTOMER_VIEW_STYLES.COMMON_STYLES.COUNT_ANNOTATION.cardContent}>
        <div className={CUSTOMER_VIEW_STYLES.COMMON_STYLES.COUNT_ANNOTATION.metricsContainer}>
          {metricdata.map((cardData, index) => (
            <CustomMetricsCard
              key={`metric-card-${index}`}
              title={cardData.title}
              description={cardData.description}
              currentValue={cardData.currentValue}
              abbrevatedValue={cardData.abbrevatedValue}
              color={cardData.color}
            />
          ))}
        </div>
        <AIInsightsButtonWithPanel
          metricId="4"
          panelTitle="Support Request Insights"
          buttonStyle={{ marginLeft: "auto", marginRight: 8 }}
          tenantId={props.tenantId}
          tenantName={props.tenantName}
        />
      </div>
    </FluentProvider>
  );

  return (
    <>
      <div className={CUSTOMER_VIEW_STYLES.COMMON_STYLES.COUNT_ANNOTATION.overviewContainer}>
        <DocumentCard className={CUSTOMER_VIEW_STYLES.COMMON_STYLES.COUNT_ANNOTATION.mainContainerStyle}>
          {props.loading ? (
            <CustomShimmer rowCount={1} columnCount={4} styles={PRODUCT_VIEW_STYLES.OVERVIEW_SECTOIN.shimmerStyles} />
          ) : (
            displayContentsWithButton
          )}
        </DocumentCard>
      </div>
      {!props.loading && props.options.originalData.length > 0 && (
        <WarningBanner message="Note: Only Total Volume and Enterprise Cases are applicable for open cases. All the other metrics are applicable for closed cases only." />
      )}
      <div className={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CLASS_NAMES.customCardContainer}>
        <CustomCardWithMetrics
          enableUrlParams
          loading={props.loading}
          metricFilters={metricFilters}
          onDataPointClick={handleDataPointClick}
          onResetFilters={handleResetFilters}
          options={props.options}
          yAxisValues={yAxisValues}
        />
      </div>
    </>
  );
};

export default SupportChart;
